/**
 * Authoritative Forensic Score Tiers and Diagnostic Rubric
 * Single source of truth shared across ScoreInterpretationModal and LegalHubPage.
 */

export interface ScoreTier {
  id: string;
  range: string;
  min: number;
  max: number;
  badge: string;
  title: string;
  description: string;
  recommendedAction: string;
  colorScheme: {
    border: string;
    bg: string;
    titleColor: string;
    badgeBg: string;
    badgeText: string;
    iconColor: string;
    bodyText: string;
  };
}

export const FORENSIC_SCORE_TIERS: ScoreTier[] = [
  {
    id: "human",
    range: "0% – 24%",
    min: 0,
    max: 24,
    badge: "High Authenticity",
    title: "Likely Human-Written",
    description:
      "The text exhibits natural burstiness (a healthy mix of punchy short sentences and sprawling complex clauses), rich lexical diversity, and varied transitional syntax. Typical of authentic human authorship, creative composition, and original thought.",
    recommendedAction: "No further integrity verification required. Proceed normally.",
    colorScheme: {
      border: "border-emerald-200 dark:border-emerald-900/60",
      bg: "bg-emerald-50/50 dark:bg-emerald-950/20",
      titleColor: "text-emerald-950 dark:text-emerald-200",
      badgeBg: "bg-emerald-100 dark:bg-emerald-900/60",
      badgeText: "text-emerald-800 dark:text-emerald-200",
      iconColor: "text-emerald-600 dark:text-emerald-400",
      bodyText: "text-emerald-900/90 dark:text-emerald-300/90"
    }
  },
  {
    id: "light-polish",
    range: "25% – 49%",
    min: 25,
    max: 49,
    badge: "Light Polish",
    title: "Predominantly Human / Assistive Revisions",
    description:
      "Predominantly authentic human prose with localized formulaic phrasing, standard academic transitions, or assistive grammar tools (e.g., spellcheckers or grammar suggestions). Natural rhythm dominates with isolated uniform sequences.",
    recommendedAction: "Treat as authentic draft. In academic contexts, affirm student authorship.",
    colorScheme: {
      border: "border-blue-200 dark:border-blue-900/60",
      bg: "bg-blue-50/50 dark:bg-blue-950/20",
      titleColor: "text-blue-950 dark:text-blue-200",
      badgeBg: "bg-blue-100 dark:bg-blue-900/60",
      badgeText: "text-blue-800 dark:text-blue-200",
      iconColor: "text-blue-600 dark:text-blue-400",
      bodyText: "text-blue-900/90 dark:text-blue-300/90"
    }
  },
  {
    id: "ambiguous",
    range: "50% – 74%",
    min: 50,
    max: 74,
    badge: "Human Review Required",
    title: "Hybrid / Inconclusive Signal",
    description:
      "Mixed stylometric signature. Common causes include: (1) Hybrid co-writing where a human wrote an outline and used AI for polishing, (2) Extensive machine paraphrasing, (3) Formulaic academic templates (e.g. standardized lab reports), or (4) Non-native English (ESL) authors using standardized syntax.",
    recommendedAction:
      "Do not penalize or assume misconduct. Conduct a formative student conference to inspect outline drafts, research notes, and version history.",
    colorScheme: {
      border: "border-amber-200 dark:border-amber-900/60",
      bg: "bg-amber-50/50 dark:bg-amber-950/20",
      titleColor: "text-amber-950 dark:text-amber-200",
      badgeBg: "bg-amber-100 dark:bg-amber-900/60",
      badgeText: "text-amber-800 dark:text-amber-200",
      iconColor: "text-amber-600 dark:text-amber-400",
      bodyText: "text-amber-900/90 dark:text-amber-300/90"
    }
  },
  {
    id: "ai",
    range: "75% – 100%",
    min: 75,
    max: 100,
    badge: "High AI Pattern Density",
    title: "Likely AI-Generated",
    description:
      "The document displays uniform sentence lengths (monotonous cadence), low lexical surprisal (predictable token sequences), depressed vocabulary variance, and heavy clustering of formulaic transitions typical of autoregressive language models.",
    recommendedAction:
      "Inspect sentence-by-sentence attributions in the report. Request the author's progressive draft history or Google Docs revision logs before initiating formal inquiries.",
    colorScheme: {
      border: "border-rose-200 dark:border-rose-900/60",
      bg: "bg-rose-50/50 dark:bg-rose-950/20",
      titleColor: "text-rose-950 dark:text-rose-200",
      badgeBg: "bg-rose-100 dark:bg-rose-900/60",
      badgeText: "text-rose-800 dark:text-rose-200",
      iconColor: "text-rose-600 dark:text-rose-400",
      bodyText: "text-rose-900/90 dark:text-rose-300/90"
    }
  }
];

export interface ForensicMetricGlossaryItem {
  key: string;
  name: string;
  plainEnglishDescription: string;
  humanPattern: string;
  aiPattern: string;
}

export const FORENSIC_METRICS_GLOSSARY: ForensicMetricGlossaryItem[] = [
  {
    key: "burstiness",
    name: "Burstiness (Sentence Rhythm Variance)",
    plainEnglishDescription: "Measures variation and dynamism in sentence lengths throughout the document.",
    humanPattern: "Dynamic mix of short punchy statements and long, multi-clause explanations.",
    aiPattern: "Consistently uniform and monotonous sentence lengths (flat rhythm)."
  },
  {
    key: "repetitionScore",
    name: "Repetition & Formulaic Syntax",
    plainEnglishDescription: "Calculates the presence of predictable syntactic transitions and structural n-grams.",
    humanPattern: "Organic transitions, occasional grammatical idiosyncrasies, and personal idioms.",
    aiPattern: "Clustering of stock transition phrases ('it is important to note', 'delves into', 'testament to')."
  },
  {
    key: "lexicalDiversity",
    name: "Lexical Diversity (Type-Token Ratio)",
    plainEnglishDescription: "Proportion of unique vocabulary relative to total token count.",
    humanPattern: "Rich vocabulary with occasional rare synonyms and creative word choices.",
    aiPattern: "Safe, repeated high-frequency vocabulary optimized for statistical median likelihood."
  },
  {
    key: "entropy",
    name: "Information Entropy & Surprisal",
    plainEnglishDescription: "Shannon information entropy and predictability of character and word transitions.",
    humanPattern: "Higher entropy reflecting unexpected semantic turns and varied prose.",
    aiPattern: "Lower entropy matching predictable maximum-likelihood generation paths."
  }
];
